import React from 'react';
import { Dismiss20Regular, Wand16Regular } from '@fluentui/react-icons';
import { Button } from '@fluentui/react-components';
import { useIRLEvidenceAsk } from '../../hooks/useIRLEvidenceAsk';
import { groupEvidenceCitations } from '../../utils/irlEvidenceGrouping';
import {
    extractSuggestedManagementAnswer,
    resolveFindEvidenceVerdict,
} from '../../utils/findEvidenceVerdict';
import { htmlToPlainText } from '../../utils/commonUtils';
import IRLEvidencePipelineLoader from './IRLEvidencePipelineLoader';
import styles from './irlEvidence.module.css';

function VerdictBanner({ verdict }) {
    return (
        <section className={styles.simpleVerdict} data-tone={verdict.tone} aria-label="Match result">
            <p className={styles.simpleVerdictLabel}>Result</p>
            <h3 className={styles.simpleVerdictTitle}>{verdict.label}</h3>
            <p className={styles.simpleVerdictSummary}>{verdict.summary}</p>
            <p className={styles.simpleVerdictMeta}>
                {verdict.vdrDocumentCount} VDR document chunk{verdict.vdrDocumentCount === 1 ? '' : 's'} reviewed
                {verdict.assessment?.source === 'irl_claim_validation' ? ' · VDR-grounded check' : ''}
            </p>
        </section>
    );
}

function VdrEvidenceList({ entries, onOpenFile }) {
    if (!entries?.length) {
        return <p className={styles.emptyHint}>No VDR documents returned for this workstream.</p>;
    }

    return (
        <ul className={styles.simpleEvidenceList}>
            {entries.map((entry, i) => (
                <li key={`vdr-${entry.citation?.ref ?? i}`} className={styles.simpleEvidenceItem}>
                    <span className={styles.simpleEvidenceTitle}>{entry.displayTitle}</span>
                    {entry.linkedChunk?.chunk && (
                        <p className={styles.simpleEvidenceExcerpt}>
                            {String(entry.linkedChunk.chunk).slice(0, 280)}
                            {String(entry.linkedChunk.chunk).length > 280 ? '…' : ''}
                        </p>
                    )}
                    {entry.linkedChunk?.file_id && onOpenFile && (
                        <button type="button" className={styles.simpleLinkBtn} onClick={() => onOpenFile(entry.linkedChunk)}>
                            Open in data room
                        </button>
                    )}
                </li>
            ))}
        </ul>
    );
}

/**
 * Minimal Find Evidence sidebar — VDR is source of truth; three verdicts only.
 */
const IRLEvidencePanel = ({ irlRow, workstreamName, handleOpenDRFile, onClose, onUseSuggestedAnswer }) => {
    const { loading, error, result, runEvidenceValidation, reset } = useIRLEvidenceAsk({
        irlRow,
        workstreamName,
    });

    const irlRef = [irlRow?.prefix, irlRow?.item, irlRow?.sub_item].filter(v => v != null && v !== '').join('.');
    const autoRunKeyRef = React.useRef(null);

    React.useEffect(() => {
        if (!irlRow?.irresponse) return;
        const key = `${irlRow.id}-${irlRow.irresponse?.id ?? 'r'}`;
        if (autoRunKeyRef.current === key) return;
        autoRunKeyRef.current = key;
        void runEvidenceValidation();
    }, [irlRow?.id, irlRow?.irresponse?.id, runEvidenceValidation]);

    React.useEffect(() => {
        return () => {
            autoRunKeyRef.current = null;
            reset();
        };
    }, [reset]);

    const diligenceQuestion = htmlToPlainText(irlRow?.request || '').trim();
    const managementAnswer = htmlToPlainText(irlRow?.irresponse?.answer || '').trim();

    const verdict = result ? resolveFindEvidenceVerdict(result, { sourceIrlId: irlRow?.id }) : null;
    const grouped = result
        ? groupEvidenceCitations(result.citations || [], result.chunks || [], {
              sourceIrlId: irlRow?.id,
              supportVerdict: verdict?.assessment?.irlValidation?.support_verdict,
          })
        : null;
    const vdrEntries = (grouped?.documentary || []).slice(0, 5);
    const suggestedAnswer = result ? extractSuggestedManagementAnswer(result, irlRow) : '';

    const openVdrChunk = chunk => {
        if (!chunk?.file_id || !handleOpenDRFile) return;
        handleOpenDRFile({
            ...irlRow,
            referenced_file: chunk.file_id,
            workstream: chunk.workstream_id ?? irlRow?.workstream,
        });
    };

    return (
        <div className={styles.splitPanelColumn}>
            <div className={styles.splitPanelHeader}>
                <div>
                    <h2 className={styles.panelHeaderTitle}>Find evidence</h2>
                    <p className={styles.panelHeaderSub}>
                        {irlRef} · {workstreamName || 'Workstream'}
                    </p>
                </div>
                <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={onClose} aria-label="Close" />
            </div>

            <div className={styles.splitPanelScroll}>
                <div className={styles.panelRoot}>
                    {error && <div className={styles.errorBanner}>{error}</div>}

                    {loading && <IRLEvidencePipelineLoader active={loading} />}

                    {!loading && !result && !error && (
                        <p className={styles.emptyHint}>
                            Searching the data room for this question, then checking your management response.
                        </p>
                    )}

                    <section className={styles.simpleClaimBlock} aria-label="Claim">
                        <p className={styles.simpleClaimLabel}>Question</p>
                        <p className={styles.simpleClaimText}>{diligenceQuestion || '—'}</p>
                        <p className={styles.simpleClaimLabel}>Your response</p>
                        <p className={styles.simpleClaimText}>{managementAnswer || '—'}</p>
                    </section>

                    {!loading && result && verdict && (
                        <>
                            <VerdictBanner verdict={verdict} />

                            <section className={styles.section} aria-label="VDR evidence">
                                <h4 className={styles.sectionTitle}>Data room evidence</h4>
                                <p className={styles.sectionHint}>Source of truth — management response is checked against these files.</p>
                                <VdrEvidenceList entries={vdrEntries} onOpenFile={openVdrChunk} />
                            </section>

                            {suggestedAnswer && (
                                <section className={styles.simpleSuggested} aria-label="Suggested answer">
                                    <h4 className={styles.sectionTitle}>AI suggested response</h4>
                                    <p className={styles.sectionHint}>
                                        Grounded in VDR documents. Review before replacing your answer.
                                    </p>
                                    <div className={styles.simpleSuggestedBody}>{suggestedAnswer}</div>
                                    {onUseSuggestedAnswer && (
                                        <Button
                                            appearance="primary"
                                            onClick={() => onUseSuggestedAnswer(suggestedAnswer)}>
                                            Use as management response
                                        </Button>
                                    )}
                                </section>
                            )}

                            {grouped?.relatedIrl?.length > 0 && (
                                <p className={styles.simpleFootnote}>
                                    {grouped.relatedIrl.length} related IRL item
                                    {grouped.relatedIrl.length === 1 ? '' : 's'} retrieved for context only (not used as proof).
                                </p>
                            )}
                        </>
                    )}

                    <div className={styles.panelActions}>
                        {!loading && result && (
                            <Button
                                appearance="secondary"
                                icon={<Wand16Regular />}
                                onClick={() => void runEvidenceValidation()}>
                                Re-run
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IRLEvidencePanel;
